// Signing in by email, as today's welcome does (welcome/Welcome.tsx there):
// an address, then "check your email" until the link is opened. The link
// finishes in the Mac app on the shell's `poulpe-email-link` event, which
// the store hears, or in a browser on the link's own page, `/signin/:attempt`,
// whose fragment holds the token: it is taken out of the address at once,
// before anything is sent (people/emailLink.ts).
import { useEffect, useId, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BoardLogo } from '../components/Icons'
import { Loading } from '../components/layout'
import { inMacShell } from '../lib/shell'
import { settingsPath } from '../settings/routing'
import { useStore } from '../store/context'
import { emailLinkProblemText, type EmailLinkOutcome, type EmailLinkState } from '../store/store'
import { dropFragment, tokenOf } from './emailLink'
import { emailWords as w, sentAround } from './words'

/** The address's field: its name read by screen readers, or shown above it beside other fields. */
export function EmailField({ value, onChange, busy, labelled = false, autoFocus = false }: { value: string; onChange: (value: string) => void; busy: boolean; labelled?: boolean; autoFocus?: boolean }) {
  const field = useId()
  const input = (
    <input
      id={field}
      className="sign-in-input"
      type="email"
      name="email"
      autoComplete="email"
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      placeholder={labelled ? undefined : w.field}
      value={value}
      readOnly={busy}
      autoFocus={autoFocus}
      onChange={(event) => onChange(event.target.value)}
    />
  )
  if (labelled) {
    return (
      <label className="sign-in-field">
        <span>{w.field}</span>
        {input}
      </label>
    )
  }
  return (
    <>
      <label className="visually-hidden" htmlFor={field}>
        {w.field}
      </label>
      {input}
    </>
  )
}

/** "We sent a link to …", with the address in the text's own ink, and where to open it. */
export function SentLine({ address }: { address: string }) {
  const [before, after] = sentAround()
  return (
    <>
      {before}
      <strong>{address}</strong>
      {after}
      <br />
      {inMacShell() ? w.check.openOnMac : w.check.openInBrowser}
    </>
  )
}

/** A link is on its way, or nothing is: the same words either way. Another address starts again. */
export function CheckEmail({ address, onOther }: { address: string; onOther: () => void }) {
  return (
    <section className="sign-in-card" aria-labelledby="check-email-title">
      <BoardLogo size={48} />
      <div className="sign-in-head" aria-live="polite" aria-atomic="true">
        <h1 id="check-email-title">{w.check.title}</h1>
        <p className="sign-in-line">
          <SentLine address={address} />
        </p>
      </div>
      <button type="button" className="sign-in-quiet" onClick={onOther}>
        {w.check.other}
      </button>
    </section>
  )
}

/** While signed out: the link being finished, or why it did not sign in, and the way to start again. */
export function EmailLinkScreen({ state, onRestart }: { state: EmailLinkState | null; onRestart: () => void }) {
  if (!state || state.phase === 'finishing') {
    return (
      <section className="sign-in-card" aria-labelledby="email-link-title" aria-busy="true">
        <BoardLogo size={48} />
        <div className="sign-in-head" aria-live="polite" aria-atomic="true">
          <h1 id="email-link-title">{w.link.finishing}</h1>
        </div>
      </section>
    )
  }
  return <LinkFailed text={emailLinkProblemText({ ok: false, problem: state.problem, message: state.message })} onRestart={onRestart} />
}

/** Why the link did not work, and the way to start again; signed in already, no sign-in heading. */
function LinkFailed({ text, onRestart, signedIn = false }: { text: string; onRestart: () => void; signedIn?: boolean }) {
  return (
    <section className="sign-in-card" aria-labelledby={signedIn ? undefined : 'email-link-title'}>
      <BoardLogo size={48} />
      {!signedIn && (
        <div className="sign-in-head">
          <h1 id="email-link-title">Sign in to Poulpe</h1>
        </div>
      )}
      <div className="sign-in-form">
        <p className="sign-in-problem" role="alert">
          {text}
        </p>
        <button type="button" className="button button-primary sign-in-button" onClick={onRestart}>
          {w.link.restart}
        </button>
      </div>
    </section>
  )
}

/**
 * The link's page, `/signin/:attempt` with the token in its fragment: the
 * token is read once, the fragment taken out of the browser's address, then
 * the link finished (`store.finishEmailLink`, once whoever asks). What it
 * came to, once it has.
 */
export function useEmailLink(attempt: string | null): EmailLinkOutcome | null {
  const store = useStore()
  const navigate = useNavigate()
  const { hash, pathname, search } = useLocation()
  // Each link's token, as its address first showed it: the fragment is gone once read.
  const tokens = useRef(new Map<string, string | null>())
  if (attempt && !tokens.current.has(attempt)) tokens.current.set(attempt, tokenOf(hash))
  const [outcome, setOutcome] = useState<EmailLinkOutcome | null>(null)
  useEffect(() => {
    if (!attempt) return
    dropFragment()
    let live = true
    void store.finishEmailLink(attempt, tokens.current.get(attempt) ?? '').then((finished) => {
      if (live) setOutcome(finished)
    })
    return () => {
      live = false
    }
  }, [store, attempt])
  // And out of the router's own address, which would otherwise put it back with its next change.
  useEffect(() => {
    if (attempt && hash) navigate({ pathname, search }, { replace: true })
  }, [navigate, attempt, hash, pathname, search])
  return outcome
}

/**
 * The link's page while signed in: once it has signed in, or added the
 * address, the page it was asked from. If not, why, and the way back to
 * the account, where an address is added.
 */
export function EmailLinkPage() {
  const { attempt = '' } = useParams()
  const outcome = useEmailLink(attempt)
  const navigate = useNavigate()
  useEffect(() => {
    if (outcome?.ok) navigate(outcome.back ?? '/', { replace: true })
  }, [outcome, navigate])
  if (!outcome || outcome.ok) return <Loading />
  return (
    <div className="sign-in-page">
      <LinkFailed text={emailLinkProblemText(outcome)} onRestart={() => navigate(settingsPath('you', 'account'), { replace: true })} signedIn />
    </div>
  )
}
